import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-a-friend",
  title: "A prayer for a friend who is going through something",
  h1: "A prayer for a friend",
  description:
    "A prayer for somebody else's hard week, four passages on carrying each other, and what to do instead of saying you will pray for them.",
  targets: [
    "prayer for a friend",
    "prayer for someone going through a hard time",
    "prayer for a friend in need",
    "how to pray for someone else",
  ],
  published: "2026-09-03",
  standfirst:
    "Praying for somebody is easy to say and easy to forget. This is the version you actually do, and the thing to do alongside it.",
  takeaways: [
    "Pray for them now, in the message thread, before you promise to. A prayer said immediately is worth ten promised ones.",
    "Galatians 6:2 says bear one another's burdens — a physical verb about weight, which implies showing up rather than only asking.",
    "Do not pray for them in a way that assumes you know what they need. You usually do not, and the assumption shows.",
  ],
  when: [
    "The moment you read the message, before you reply.",
    "For somebody in a situation you cannot help with at all.",
    "When you have said you would pray and have not, again.",
    "For a friend who is making a decision you think is a mistake.",
  ],
  prayer: [
    "God, this is happening to them and I cannot do anything about it, which is most of why I am here.",
    "Be with them tonight specifically, in the hour when it is worst, which they have not told me about but you know.",
    "I do not know what they need. I have a theory and I am fairly attached to it. Give them what is actually needed rather than what I have decided.",
    "Show me the practical thing — the meal, the lift, the message that does not ask them to perform being fine — and let me do it without needing to be thanked.",
    "And if all I can do is stay, let me be somebody who stays after the first fortnight, when everyone else has gone back to normal.",
  ],
  short:
    "God, be with them tonight in the hour when it is worst. Show me the practical thing.",
  honest: [
    "This prayer is not a substitute for turning up. Galatians describes carrying weight, and weight is not carried by intention — if you can cook, drive or sit in a waiting room, the prayer is the beginning rather than the whole contribution.",
    "It also does not give you standing to advise. Praying for somebody does not make you responsible for their decisions, and a prayer that turns into a case for the course of action you prefer has stopped being intercession.",
    "And it will not always change the outcome. People are prayed for faithfully and things still go badly, and pretending otherwise sets your friend up to conclude that they were not prayed for well enough.",
  ],
  verses: [
    {
      verse: {
        text: "Bear one another's burdens, and so fulfill the law of Christ.",
        ref: "Galatians 6:2",
      },
      note: "A physical verb about weight — the same word used for carrying a load — which makes it hard to satisfy from a distance. Three verses later Paul says each will bear his own load, using a different word for a soldier's pack, and the two are not a contradiction: some things are yours to carry and some are too heavy for one person, and friendship is knowing which is which.",
    },
    {
      verse: {
        text: "A friend loves at all times; and a brother is born for adversity.",
        ref: "Proverbs 17:17",
      },
      note: "At all times is doing the work, and the second half explains what it is guarding against: the friend who is present for the good stretch and unreachable during the bad one. Adversity is named as the moment the relationship is actually for, which reframes a difficult fortnight as the point rather than an interruption.",
    },
    {
      verse: {
        text: "Greater love has no one than this, that someone lay down his life for his friends.",
        ref: "John 15:13",
      },
      note: "Usually quoted about dying, and said the night before he did. But the paragraph around it is about friendship as a category — he has just stopped calling them servants — and the laying down being described is a whole posture, of which the death is the extreme instance. Most weeks it looks like an evening you wanted for yourself.",
    },
    {
      verse: {
        text: "Two are better than one, because they have a good reward for their labor. For if they fall, the one will lift up his fellow; but woe to him who is alone when he falls, and doesn't have another to lift him up.",
        ref: "Ecclesiastes 4:9–10",
      },
      note: "The most unsentimental case for friendship in Scripture, made by the book least interested in comforting anybody. The argument is about falling: not who you enjoy, but who is close enough to pick you up. That is a different criterion from the one most friendships are chosen by.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Pray it now, not later",
      id: "pray-it-now-not-later",
    },
    {
      type: "p",
      text: "I will be praying for you is one of the most commonly broken promises in Christian life, and it is broken innocently — the message arrives, the intention is real, and then Tuesday happens.",
    },
    {
      type: "p",
      text: "The fix is to do it in the ten seconds before replying. Then the reply can honestly say I just prayed, which is also a better thing to receive.",
    },
    {
      type: "h2",
      text: "Then do the practical thing",
      id: "then-do-the-practical-thing",
    },
    {
      type: "p",
      text: "The instruction in Galatians is about weight, and weight is moved by hands. Food, a lift, a bill, an afternoon of childcare, a message that does not require a cheerful answer.",
    },
    {
      type: "note",
      text: "Do not ask what they need — an exhausted person cannot answer that. Offer a specific thing at a specific time and let them decline it.",
    },
    {
      type: "h2",
      text: "Stay past the fortnight",
      id: "stay-past-the-fortnight",
    },
    {
      type: "p",
      text: "Attention after a crisis is intense for about two weeks and then goes. The person is usually worse in month three than they were in week one, and by then almost nobody is asking.",
    },
    {
      type: "p",
      text: "Put a date in your calendar for six weeks out. It is unromantic, it feels like cheating, and it is the single most useful thing in this page — the same principle as {any practice that survives feeling}.",
      links: {
        "any practice that survives feeling":
          "/prayers/prayer-when-you-cant-pray",
      },
    },
  ],
  faq: [
    {
      q: "How do I pray for a friend going through a hard time?",
      a: "Pray it immediately rather than promising to, ask for what they actually need rather than what you have decided they need, and then do one specific practical thing without waiting to be asked.",
    },
    {
      q: "What do you say to someone instead of I will pray for you?",
      a: "Pray in the ten seconds before you reply, then say you just did. It costs the same and it is true.",
    },
    {
      q: "Which Bible verse is about supporting a friend?",
      a: "Galatians 6:2 — bear one another's burdens — with the caution that the verb is about physical weight, and Ecclesiastes 4:9–10 for the most unsentimental case: the point of a friend is somebody close enough to pick you up.",
    },
  ],
  relatedPrayers: ["prayer-for-loneliness", "prayer-for-strength"],
  related: ["phone-rules-for-christian-families"],
  relatedTopics: [
    "bible-verses-about-grief",
    "bible-verses-about-strength",
    "bible-verses-about-encouragement",
  ],
  relatedPassages: ["philippians-4-6-7"],
};
